import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import doctorService from '../../services/doctorService';

export const fetchDoctors = createAsyncThunk('doctors/fetchDoctors', async (params, { rejectWithValue }) => {
  try {
    const response = await doctorService.getAllDoctors(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch doctors');
  }
});

export const createDoctor = createAsyncThunk('doctors/createDoctor', async (data, { rejectWithValue }) => {
  try {
    const response = await doctorService.createDoctor(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create doctor');
  }
});

export const updateDoctor = createAsyncThunk('doctors/updateDoctor', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await doctorService.updateDoctor(id, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update doctor');
  }
});

export const deleteDoctor = createAsyncThunk('doctors/deleteDoctor', async (id, { rejectWithValue }) => {
  try {
    await doctorService.deleteDoctor(id);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete doctor');
  }
});

export const toggleDoctorStatus = createAsyncThunk('doctors/toggleStatus', async (id, { rejectWithValue }) => {
  try {
    const response = await doctorService.toggleStatus(id);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to toggle status');
  }
});

export const verifyDoctor = createAsyncThunk('doctors/verify', async (id, { rejectWithValue }) => {
  try {
    const response = await doctorService.verifyDoctor(id);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to verify doctor');
  }
});

const doctorsSlice = createSlice({
  name: 'doctors',
  initialState: {
    doctors: [],
    totalDoctors: 0,
    totalPages: 0,
    isLoading: false,
    success: false,
    error: null,
  },
  reducers: {
    resetSuccess: (state) => {
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDoctors.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchDoctors.fulfilled, (state, action) => {
        state.isLoading = false;
        state.doctors = action.payload.data;
        state.totalDoctors = action.payload.pagination?.totalItems || 0;
        state.totalPages = action.payload.pagination?.totalPages || 0;
      })
      .addCase(fetchDoctors.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createDoctor.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createDoctor.fulfilled, (state, action) => {
        state.isLoading = false;
        state.doctors.unshift(action.payload.data);
        state.success = true;
      })
      .addCase(createDoctor.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateDoctor.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateDoctor.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.doctors.findIndex((d) => d._id === action.payload.data._id);
        if (index !== -1) {
          state.doctors[index] = action.payload.data;
        }
        state.success = true;
      })
      .addCase(updateDoctor.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteDoctor.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteDoctor.fulfilled, (state, action) => {
        state.isLoading = false;
        state.doctors = state.doctors.filter((d) => d._id !== action.payload);
        state.success = true;
      })
      .addCase(deleteDoctor.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(toggleDoctorStatus.fulfilled, (state, action) => {
        const index = state.doctors.findIndex((d) => d._id === action.payload.data._id);
        if (index !== -1) {
          state.doctors[index] = action.payload.data;
        }
      })
      .addCase(verifyDoctor.fulfilled, (state, action) => {
        const index = state.doctors.findIndex((d) => d._id === action.payload.data._id);
        if (index !== -1) {
          state.doctors[index] = action.payload.data;
        }
      });
  },
});

export const { resetSuccess, clearError } = doctorsSlice.actions;
export default doctorsSlice.reducer;
